import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "./components/AdminLayout";
import { message, Pagination, Select } from "antd";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import "./AdminUsers.css";

const { Option } = Select;

const clampAmount = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  // safety cap (UI level). backend must also cap.
  return Math.min(n, 1000000);
};

const AdminUsers = () => {
  const [allUser, setAllUser] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredUsers, setFilteredUsers] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(50);

  // ✅ separate loading states
  const [blockingId, setBlockingId] = useState(null);
  const [balanceUpdatingId, setBalanceUpdatingId] = useState(null);
  const [resellerUpdatingId, setResellerUpdatingId] = useState(null); // ✅ NEW

  // ✅ per-user input amounts
  const [amountByUser, setAmountByUser] = useState({}); // { userId: "123" }

  const authHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: "Bearer " + token } : {};
  };

  const safeDateTime = (value) => {
    if (!value) return "-";
    const t = new Date(value).getTime();
    if (!Number.isFinite(t)) return "-";
    return new Date(t).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const safeDate = (value) => {
    if (!value) return "";
    const t = new Date(value).getTime();
    if (!Number.isFinite(t)) return "";
    return new Date(t).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getAllUser = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return message.error("Token missing. Please login again.");

      const res = await axios.get("/api/admin/get-all-users", {
        headers: authHeaders(),
      });

      if (res.data?.success) {
        setAllUser(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        message.error(res.data?.message || "Failed to fetch users");
      }
    } catch (error) {
      console.log(error);
      message.error(error?.response?.data?.message || "Failed to fetch users");
    }
  };

  useEffect(() => {
    getAllUser();
    // eslint-disable-next-line
  }, []);

  // Search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(null);
      setCurrentPage(1);
      return;
    }

    const q = searchQuery.trim().toLowerCase();
    const filtered = (allUser || []).filter((u) =>
      String(u?.email || "").toLowerCase().includes(q)
    );

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [searchQuery, allUser]);

  // Sort latest first
  const sortedUsers = useMemo(() => {
    const base = filteredUsers ?? allUser ?? [];
    return [...base].sort((a, b) => {
      const da = new Date(a?.createdAt || a?.created || 0).getTime();
      const db = new Date(b?.createdAt || b?.created || 0).getTime();
      return db - da;
    });
  }, [filteredUsers, allUser]);

  const totalUsers = sortedUsers.length;

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser);

  // ✅ Block update (existing)
  const updateBlock = async (userId, nextBlock, isAdmin) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return message.error("Token missing. Please login again.");

      if (isAdmin) return message.error("Cannot block admin user");

      if (nextBlock !== "yes" && nextBlock !== "no") {
        return message.error("Invalid block value");
      }

      setBlockingId(userId);

      const res = await axios.post(
        "/api/admin/edit-user",
        { _id: userId, block: nextBlock },
        { headers: authHeaders() }
      );

      if (res.data?.success) {
        setAllUser((prev) =>
          (prev || []).map((u) => (u._id === userId ? { ...u, block: nextBlock } : u))
        );
        message.success("User updated");
      } else {
        message.error(res.data?.message || "Failed to update user");
      }
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Update failed");
    } finally {
      setBlockingId(null);
    }
  };

  // ✅ Reseller yes/no update (NEW)
  const updateReseller = async (userId, nextReseller, isAdmin) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return message.error("Token missing. Please login again.");

      if (isAdmin) return message.error("Cannot modify admin user");

      if (nextReseller !== "yes" && nextReseller !== "no") {
        return message.error("Invalid reseller value");
      }

      setResellerUpdatingId(userId);

      const res = await axios.post(
        "/api/admin/edit-user",
        { _id: userId, reseller: nextReseller },
        { headers: authHeaders() }
      );

      if (res.data?.success) {
        setAllUser((prev) =>
          (prev || []).map((u) =>
            u._id === userId ? { ...u, reseller: nextReseller } : u
          )
        );
        message.success("Reseller updated");
      } else {
        message.error(res.data?.message || "Failed to update reseller");
      }
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Reseller update failed");
    } finally {
      setResellerUpdatingId(null);
    }
  };

  // ✅ Balance credit/debit (existing)
  const updateBalance = async (user, action) => {
    const userId = user?._id;
    if (!userId) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) return message.error("Token missing. Please login again.");

      if (user?.isAdmin) {
        return message.error("Cannot modify admin balance");
      }

      const raw = amountByUser[userId];
      const amt = clampAmount(raw);

      if (!amt) return message.error("Enter valid amount");

      setBalanceUpdatingId(userId);

      const res = await axios.post(
        "/api/admin/edit-user",
        {
          _id: userId,
          balanceAction: action, // "credit" | "debit"
          amount: amt,
          note: "ADMIN_PANEL",
        },
        { headers: authHeaders() }
      );

      if (res.data?.success) {
        const newBal = res.data?.data?.balance;

        setAllUser((prev) =>
          (prev || []).map((u) =>
            u._id === userId
              ? { ...u, balance: typeof newBal === "number" ? newBal : u.balance }
              : u
          )
        );

        // clear input
        setAmountByUser((prev) => ({ ...prev, [userId]: "" }));

        message.success(action === "credit" ? "Balance added" : "Balance deducted");
      } else {
        message.error(res.data?.message || "Balance update failed");
      }
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Balance update failed");
    } finally {
      setBalanceUpdatingId(null);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-users-container">
        <div className="page-title">
          <h3 className="m-0">Customers</h3>
          <h6 className="m-0">Total Users - {totalUsers}</h6>
        </div>
        <hr />

        <div className="table-container">
          <div className="tools">
            <div className="form-fields">
              <SearchIcon className="text-dark me-2" />
              <input
                type="search"
                name="search"
                placeholder="Search by email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <Select
              value={String(usersPerPage)}
              className="pageSizeSelect"
              onChange={(value) => {
                setUsersPerPage(Number(value));
                setCurrentPage(1);
              }}
            >
              <Option value="10">10</Option>
              <Option value="20">20</Option>
              <Option value="50">50</Option>
              <Option value="100">100</Option>
            </Select>
          </div>

          <div className="table-scroll">
            <table className="table user-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Balance</th>
                  <th>Adjust Balance</th>
                  <th>Last Login</th>
                  <th>Admin</th>
                  <th>Block</th>
                  <th>Created At</th>
                </tr>
              </thead>

              <tbody>
                {currentUsers.map((user) => {
                  const uid = user?._id;
                  const isBusy =
                    blockingId === uid ||
                    balanceUpdatingId === uid ||
                    resellerUpdatingId === uid;

                  return (
                    <tr key={uid}>
                      <td>
                        <small>{user?.fname || "-"}</small>
                      </td>
                      <td>
                        <small>{user?.email || "-"}</small>
                      </td>
                      <td>
                        <small>{typeof user?.balance === "number" ? user.balance : 0}</small>
                      </td>

                      {/* ✅ Credit / Deduct */}
                      <td>
                        <div className="balanceTools">
                          <input
                            className="balanceInput"
                            type="number"
                            min="1"
                            step="1"
                            inputMode="numeric"
                            placeholder="Amount"
                            value={amountByUser[uid] ?? ""}
                            disabled={isBusy || user?.isAdmin}
                            onChange={(e) =>
                              setAmountByUser((prev) => ({
                                ...prev,
                                [uid]: e.target.value,
                              }))
                            }
                          />

                          <button
                            type="button"
                            className="balBtn balAdd"
                            disabled={isBusy || user?.isAdmin}
                            onClick={() => updateBalance(user, "credit")}
                            title="Add balance"
                          >
                            Add
                          </button>

                          <button
                            type="button"
                            className="balBtn balDeduct"
                            disabled={isBusy || user?.isAdmin}
                            onClick={() => updateBalance(user, "debit")}
                            title="Deduct balance"
                          >
                            Deduct
                          </button>
                        </div>

                        {user?.isAdmin ? (
                          <small style={{ opacity: 0.7 }}>Protected</small>
                        ) : null}
                      </td>

                      <td>
                        <small>{safeDateTime(user?.lastLogin)}</small>
                      </td>

                      <td>
                        <small>{user?.isAdmin ? "Yes" : "No"}</small>
                      </td>

                      {/* ✅ NEW: Reseller dropdown 
                      <td>
                        <Select
                          value={user?.reseller === "yes" ? "yes" : "no"}
                          className="blockSelect"
                          disabled={isBusy || user?.isAdmin}
                          onChange={(v) => updateReseller(uid, v, user?.isAdmin)}
                          options={[
                            { value: "no", label: "no" },
                            { value: "yes", label: "yes" },
                          ]}
                        />
                      </td>
                      */}
                      
                      <td>
                        <Select
                          value={user?.block === "yes" ? "yes" : "no"}
                          className="blockSelect"
                          disabled={isBusy || user?.isAdmin}
                          onChange={(v) => updateBlock(uid, v, user?.isAdmin)}
                          options={[
                            { value: "no", label: "no" },
                            { value: "yes", label: "yes" },
                          ]}
                        />
                      </td>

                      <td>
                        <small>{safeDate(user?.createdAt || user?.created)}</small>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="pagination-container">
            <Pagination
              current={currentPage}
              total={totalUsers}
              pageSize={usersPerPage}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;